import * as React from "react";
import { useState, useRef, useEffect } from "react";

import { Mutation, UseMutationResult } from "@tanstack/react-query";
import * as PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { ResourceStates } from "../../Helpers/DataDefinitions";
import Icon from "../Icons";
import Button, { ButtonProps } from "./Button";

export { unwrapThunkResponse } from "../../Helpers/ThunkErrorHandling";

// Example Code

// const AForm = () => {
//   const [submissionState, trackSubmit] = useSubmitButton();

//   const onSubmit = async (data) => {
//     await postRequest(data);
//   }

//   return (
//     <form onClick={trackSubmit(onSubmit)}>
//       <SubmitButton submissionState={submissionState}>Submit</SubmitButton>;
//     </form>
//   );
// };

interface SubmitButtonOptions {
  errorTimeout?: number;
}
type SubmissionState = "LOADING" | "LOADED" | "ERROR";

export const getSubmissionStateFromMutation = (
  mutation: Pick<UseMutationResult, "status">,
): SubmissionState => {
  switch (mutation.status) {
    case "idle":
    case "success":
      return "LOADED";
    case "pending":
      return "LOADING";
    case "error":
      return "ERROR";
  }
};
export const useSubmitButton = ({
  errorTimeout,
  ...opts
}: SubmitButtonOptions = {}) => {
  const [submissionState, setSubmissionState] =
    useState<SubmissionState>("LOADED");

  // trackSubmit is trying to update the submission state which may cause a memory leak when the component it belongs to
  // has been unmounted.
  // We do this a lot with the onCompletion method unmounting the component, within the trackSubmit call.
  // We now check if the component is mounted before updating the submission state.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  const safelySetSubmissionState = (newSubmissionState: SubmissionState) => {
    if (isMounted.current) {
      setSubmissionState(newSubmissionState);
    }
  };

  const trackSubmit = (onSubmit: Function) => async (data: any) => {
    try {
      safelySetSubmissionState("LOADING");
      const response = await onSubmit(data);
      safelySetSubmissionState("LOADED");
      return response;
    } catch (error) {
      console.error(error);
      safelySetSubmissionState("ERROR");
      setTimeout(
        () => safelySetSubmissionState("LOADED"),
        errorTimeout || 1000,
      );
    }
  };

  return [submissionState, trackSubmit] as [
    SubmissionState,
    (onSubmit: Function) => (data: any) => Promise<any>,
  ];
};

interface SubmitProps {
  submissionState: SubmissionState;
  children: React.ReactNode;
  loadingMessage?: React.ReactNode;
  errorMessage?: React.ReactNode;
  className?: string;
}
export type SubmitButtonProps = SubmitProps & ButtonProps;

export type SubmitLinkProps = SubmitProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

const Unspecified = undefined;
export const SubmitLink = ({
  submissionState,
  children,
  loadingMessage,
  errorMessage,
  ...props
}: SubmitLinkProps) => {
  const { t } = useTranslation();
  const state = submissionState;

  return (
    <a {...props}>
      {state === ResourceStates.loaded && children}
      {state === ResourceStates.loading && (
        <>
          {loadingMessage || t("actions.saving")}{" "}
          <Icon
            icon="loading"
            color={Unspecified}
            size="xs"
            className="inline-block animate-spin align-middle"
          />
        </>
      )}
      {state === ResourceStates.error &&
        (errorMessage || (
          <>
            {t("actions.savingFailed")}{" "}
            <Icon
              className={Unspecified}
              color={Unspecified}
              size="base"
              icon="error"
            />
          </>
        ))}{" "}
    </a>
  );
};

export const SubmitButton = ({
  submissionState,
  children,
  loadingMessage,
  errorMessage,
  ...props
}: SubmitButtonProps) => {
  const { t } = useTranslation();
  const state = submissionState;

  return (
    <Button disabled={state === ResourceStates.loading} {...props}>
      {state === ResourceStates.loaded && children}
      {state === ResourceStates.loading && (
        <span className="flex items-center space-x-4">
          <div>{loadingMessage || t("actions.saving")}</div>
          <div>
            <Icon
              icon="loading"
              color={"current"}
              size="xs"
              className="inline-block animate-spin align-middle"
            />
          </div>
        </span>
      )}
      {state === ResourceStates.error &&
        (errorMessage || (
          <div className="flex items-center space-x-4">
            {t("actions.savingFailed")}{" "}
            <Icon
              className={Unspecified}
              color={"red-500"}
              size="base"
              icon="error"
            />
          </div>
        ))}{" "}
    </Button>
  );
};

export const DeleteButton = (props: SubmitButtonProps) => {
  const { t } = useTranslation();
  return (
    <SubmitButton
      errorMessage={t("actions.deletingFailed")}
      loadingMessage={t("actions.deleting")}
      danger
      type="button"
      disabled={props.submissionState === ResourceStates.loading}
      {...props}
    />
  );
};
SubmitButton.propTypes = {
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  tertiary: PropTypes.bool,
  danger: PropTypes.bool,
  loadingMessage: PropTypes.node,
  errorMessage: PropTypes.node,
  submissionState: PropTypes.oneOf([
    ResourceStates.loading,
    ResourceStates.loaded,
    ResourceStates.error,
  ]),
  children: PropTypes.node,
};
